import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../lib/api";

const STAR_LEVELS = [1, 2, 3, 4, 5];

function StarRow({ value, onChange, label, testId, disabled = false }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <label className="text-sm text-slate-700">{label}</label>
      <div className="flex gap-1" data-testid={testId}>
        {STAR_LEVELS.map((n) => (
          <button
            key={n}
            type="button"
            disabled={disabled}
            onClick={() => onChange(n === value ? 0 : n)}
            className={`text-xl leading-none transition-colors ${n <= (value || 0) ? "text-amber-500" : "text-slate-300"} hover:text-amber-400 disabled:cursor-not-allowed`}
            data-testid={`${testId}-star-${n}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
          >
            ★
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * @param {'employer_to_agency' | 'agency_to_employer'} direction
 */
export default function RatingModal({ submissionId, direction, ratee_name, onClose, onSubmitted }) {
  const { t } = useTranslation();
  const [overall, setOverall] = useState(0);
  const [quality, setQuality] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [responsiveness, setResponsiveness] = useState(0);
  const [paymentTimeliness, setPaymentTimeliness] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const buyerSide = direction === "employer_to_agency";
  const title = buyerSide ? t("rating.rate_agency", { name: ratee_name }) : t("rating.rate_employer", { name: ratee_name });

  const canSubmit = overall > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const body = { overall };
      if (comment.trim()) body.comment = comment.trim().slice(0, 1000);
      if (communication) body.communication = communication;
      if (buyerSide) {
        if (quality) body.quality = quality;
        if (speed) body.speed = speed;
      } else {
        if (responsiveness) body.responsiveness = responsiveness;
        if (paymentTimeliness) body.payment_timeliness = paymentTimeliness;
      }
      await api.post(`/submissions/${submissionId}/ratings`, body);
      toast.success(t("rating.thanks"));
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(formatApiErrorDetail(detail) || t("common.error_generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="rating-modal">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-slate-900" data-testid="rating-modal-title">{title}</h3>
        <p className="mt-1 text-xs text-slate-500">{t("rating.subtitle")}</p>
        <div className="mt-4 divide-y divide-slate-100">
          <StarRow value={overall} onChange={setOverall} label={t("rating.overall")} testId="rating-overall" />
          {buyerSide && <StarRow value={quality} onChange={setQuality} label={t("rating.quality")} testId="rating-quality" />}
          {buyerSide && <StarRow value={speed} onChange={setSpeed} label={t("rating.speed")} testId="rating-speed" />}
          <StarRow value={communication} onChange={setCommunication} label={t("rating.communication")} testId="rating-communication" />
          {!buyerSide && <StarRow value={responsiveness} onChange={setResponsiveness} label={t("rating.responsiveness")} testId="rating-responsiveness" />}
          {!buyerSide && <StarRow value={paymentTimeliness} onChange={setPaymentTimeliness} label={t("rating.payment_timeliness")} testId="rating-payment" />}
        </div>
        <label className="mt-4 block text-sm font-medium text-slate-700">{t("rating.comment_label")}</label>
        <textarea
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value.slice(0, 1000))}
          className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
          placeholder={t("rating.comment_placeholder")}
          data-testid="rating-comment"
        />
        <p className="mt-1 text-right text-xs text-slate-400">{comment.length}/1000</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50" data-testid="rating-cancel">
            {t("common.cancel")}
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600"
            data-testid="rating-submit"
          >
            {busy ? t("common.loading") : t("rating.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
